import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import QRCodeScanner from "./QRCodeScanner";
import "./FormPage.css";
import { useLocation } from "react-router-dom";

import content from "../static/content.json";

const OverviewPage = ({ username, firstName, setIsAuthenticated }) => {
  const [items, setItems] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [scanResult, setScanResult] = useState("");
  const [redirecting, setRedirecting] = useState(false);
  const [cameraPermission, setCameraPermission] = useState(false);
  const userId = localStorage.getItem("userId");
  const apiToken = localStorage.getItem("apiToken");
  const navigate = useNavigate();
  const staticText = content.OverviewPage;
  const [cameraFacingMode, setCameraFacingMode] = useState("environment");

  const isDemoMode = localStorage.getItem("userId") === "demo";
  const scannerComponentRef = useRef();


  const [formData, setFormData] = useState({
    form_notes: "",
    consent: false,
    id: "",
    phone: "",
    form_date: "",
    form_time: "",
    form_location: "",
    form_photo: null,
  });
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/demo") {
      alert(
        "Sie befinden sich im Demo-Modus. Sie werden zum Feedback-Formular weitergeleitet..."
      );
      setTimeout(() => {
        navigate("/feedback");
      }, 2000);
    }
  }, [location.pathname, navigate]);


  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("userId");
    localStorage.removeItem("apiToken");
  };

const handleScanQR = async () => {
  try {
    if (!cameraPermission) {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraPermission(true);
      setShowScanner(true);
      stream.getTracks().forEach((track) => track.stop());
    } else {
      setShowScanner(true);
    }
  } catch (error) {
    console.error("Camera access denied:", error);
    alert(
      "Camera access is required to scan QR codes. Please enable camera permissions in your browser settings."
    );
  }
};

const handleScan = async (scannedText) => {
  if (!scannedText) return;

  if (scannerComponentRef.current) {
    await scannerComponentRef.current.stop();
  }

  setShowScanner(false);

  if (scannedText.includes("/demo")) {
    alert("Demo-Modus erkannt. Sie werden weitergeleitet...");
    localStorage.setItem("caseId", "demo");

    setTimeout(() => {
      navigate("/form");
    }, 2000);
    return;
  }

  const match = scannedText.match(/submit\/(.+)/);
  const extractedPart = match ? match[1] : "No valid part found";
  setScanResult(extractedPart);

  if (match) {
    setRedirecting(true);
    setTimeout(() => {
      navigate("/form");
    }, 2000);
  } else {
    alert("Ungültiger QR-Code");
  }
};


  const handleCloseScanner = async () => {
    if (scannerComponentRef.current) {
      await scannerComponentRef.current.stop();
    }
    setShowScanner(false);
  };


  const handleToggleCamera = () => {
    setCameraFacingMode((prevMode) =>
      prevMode === "environment" ? "user" : "environment"
    );
  };

  const handleError = (err) => {
    console.error("QR Code Scan Error:", err);
  };

  useEffect(() => {
    if (isDemoMode) {
      const savedData = JSON.parse(localStorage.getItem("issueInfoDemo"));
      const feedbackData = JSON.parse(localStorage.getItem("feedbackDataDemo"));
      if (savedData) {
        setFormData(savedData);
        setItems([savedData]);
      }
      if (feedbackData) {
        setItems((prevItems) =>
          prevItems.map((item) => ({
            ...item,
            feedback_available: true,
          }))
        );
      }
    } else {
      axios
        .get("https://app.better-retter.de/api/patients", {
          params: {
            api_token: apiToken,
            user_id: userId,
            software: "better-retter",
          },
        })
        .then((response) => {
          setItems(response.data.patients || []);
        })
        .catch((error) => {
          console.error("There was an error fetching the items!", error);
        });
    }
  }, [apiToken, userId, isDemoMode]);

  const handleEdit = (patient_id) => {
    if (isDemoMode) {
      const savedData = JSON.parse(localStorage.getItem("issueInfoDemo"));
      if (savedData) {
        localStorage.setItem("editCaseData", JSON.stringify(savedData));
      }
    } else {
      localStorage.setItem("editCaseId", patient_id);
    }
    localStorage.setItem("isEditMode", "true");
    navigate("/form");
  };

  const handleView = (patient_id) => {
    localStorage.setItem("editCaseId", patient_id);
    navigate("/viewfeedback");
  };

  const handleDelete = (patient_id) => {
    if (isDemoMode) {
      const key = "issueInfoDemo";
      localStorage.removeItem(key);
      setItems(items.filter((item) => item.patient_id !== patient_id));
    } else {
      axios
        .delete(
          `https://app.better-retter.de/api/patient/feedbacks/${patient_id}`,
          {
            params: {
              api_token: apiToken,
              user_id: userId,
              software: "better-retter",
            },
          }
        )
        .then(() => {
          setItems(items.filter((item) => item.patient_id !== patient_id));
        })
        .catch((error) => {
          console.error("Error deleting feedback for patient:", error);
        });
    }
  };

  return (
    <div className="page-container">
      {/* Navbar */}
      <nav className="navbar">
        <div className="navbar-content">
          <h1>Hallo {username || "Max Mustermann"}!</h1>
          <div className="navbar-buttons">
            <button
              className="form-button form-button-primary"
              onClick={() => navigate("/form")}
            >
              ein Demo-Ereignis erstellen
            </button>
            <button
              className="form-button form-button-primary"
              onClick={handleScanQR}
            >
              QR-Code scannen
            </button>
            <button
              className="form-button form-button-danger"
              onClick={handleLogout}
            >
              {staticText.LogoutButtonText}
            </button>
          </div>
        </div>
      </nav>

      {/* QR Scanner */}
      {showScanner && cameraPermission && (
        <div className="scanner-container">
          <div className="scanner-indicator">
            QR-Code innerhalb des Rahmens ausrichten
          </div>

          <QRCodeScanner
            key={cameraFacingMode}
            ref={scannerComponentRef}
            facingMode={cameraFacingMode}
            onScanSuccess={handleScan}
            onScanError={handleError}
          />

          <div className="scanner-buttons">
            <button
              className="form-button form-button-danger"
              onClick={handleCloseScanner}
            >
              Abbrechen
            </button>
            <button
              className="form-button form-button-primary"
              onClick={handleToggleCamera}
            >
              Kamera wechseln
            </button>
          </div>
        </div>
      )}

      {/* Table Overview */}
      <div className="table-container">
        <table className="table-overview">
          <thead>
            <tr>
              <th>{staticText.FeedbackNumberColumnName}</th>
              <th>{staticText.DateColumnName}</th>
              {/* <th>{staticText.PlaceColumnName}</th> */}
              <th>{staticText.NoteColumnName}</th>
              <th>{staticText.isFeedbackAvailableColumnName}</th>
              <th style={{ whiteSpace: "nowrap" }}>{"Bearbeiten - Löschen"}</th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item, index) => (
                <tr key={item.patient_id || index}>
                  <td>{index + 1}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {item.form_date ||
                      formData.form_date ||
                      staticText.NoTextAvailableForSingleItem}
                  </td>
                  {/* <td>
                  {item.form_location ||
                    formData.form_location ||
                    staticText.NoTextAvailableForSingleItem}
                </td> */}
                  <td style={{ whiteSpace: "nowrap" }}>
                    {item.form_note ||
                      formData.form_note ||
                      staticText.NoTextAvailableForSingleItem}
                  </td>
                  <td>
                    {item.feedback_available > 0 ? (
                      <div
                        className="action-button view-button"
                        onClick={() => handleView(item.patient_id)}
                      >
                        {staticText.FeedbackAvailableEmoji}
                      </div>
                    ) : (
                      staticText.FeedbackNotAvailableEmoji
                    )}
                  </td>
                  <td>
                    <button
                      className="action-button edit-button"
                      onClick={() => handleEdit(item.patient_id)}
                    >
                      {staticText.EditButtonEmoji}
                    </button>
                    <button
                      className="action-button delete-button"
                      onClick={() => handleDelete(item.patient_id)}
                    >
                      {staticText.DeleteCaseEmoji}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7">{staticText.NoFeedbackAvailableMessage}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OverviewPage;
